import { StockTypeToggle } from "@fero/components";
import { PortalPageContent, PortalPageContentHeader } from "@fero/layouts";
import { AppRoutes } from "@fero/utils";
import { Alert, Box, Divider, Grid, Stack } from "@mui/material";
import type { OrganisationImportantReminders } from "adl-gen/ferovinum/app/api";
import type { TopLevelUnitType } from "adl-gen/ferovinum/app/db";
import type { SettlementCreditDetails } from "adl-gen/ferovinum/app/settlementcredit";
import { Loading } from "components/widgets/loader/loader";
import { isLoaded, type LoadingValue } from "utils/utility-types";
import { IconStockCards } from "./icon-stock-cards";
import { OrganisationCreditCards } from "./organisation-credit-cards";
import { ReminderCard } from "./reminder-card";
import {
  type StockDataDisplayInfo,
  StockListingTable,
} from "./stock-listing-table";
import { useOrganisationDashboardPageViewViewModel } from "./use-organisation-dashboard-page-view.view-model";

interface Props {
  // Stock data by top level unit type (cans, bottles, cask) and by product code
  stockDataLookup: Partial<Record<TopLevelUnitType, StockDataDisplayInfo>>;
  loadingImportantReminders: LoadingValue<OrganisationImportantReminders>;
  loadMoreStockDataMap: (
    topLevelUnitType: TopLevelUnitType,
    offset: number,
  ) => Promise<StockDataDisplayInfo["stockDataMap"] | undefined>;
}

export const OrganisationDashboardPageView = ({
  stockDataLookup,
  loadingImportantReminders,
  loadMoreStockDataMap,
}: Props) => {
  const {
    availableStockTypes,
    setTopLevelUnitType,
    initLoadingListings,
    topLevelUnitType,
    organisationCredit,
    currencyFormatter,
    loadMore,
    stockDataDisplayInfo,
  } = useOrganisationDashboardPageViewViewModel({
    stockDataLookup,
    loadMoreStockDataMap,
  });

  return (
    <PortalPageContent header={<PortalPageContentHeader title="Dashboard" />}>
      <Stack spacing={4}>
        <Box sx={{ margin: -1 }}>
          <Grid container spacing={2}>
            {isLoaded(loadingImportantReminders) &&
              loadingImportantReminders.value.expiringStock !== null && (
                <Grid item xs={12} md={3}>
                  <ReminderCard
                    title={"Review Expiring Stock"}
                    body="You have stock expiring soon. Let us know how you would like to manage it."
                    buttonProps={{
                      label: "Review now",
                      route: AppRoutes.OrganisationExpiringStock,
                    }}
                  />
                </Grid>
              )}
            {!organisationCredit.isLoading &&
              !organisationCredit.isError &&
              organisationCredit.data && (
                <Grid item xs={12} md={3}>
                  <OrganisationCreditCards
                    organisationCredit={
                      organisationCredit.data as SettlementCreditDetails
                    }
                    currencyFormatter={currencyFormatter}
                  />
                </Grid>
              )}
          </Grid>
        </Box>
        {availableStockTypes.size ? (
          <>
            {availableStockTypes.size > 1 && (
              <>
                <StockTypeToggle
                  topLevelUnitType={topLevelUnitType}
                  onChange={setTopLevelUnitType}
                  availableTypes={availableStockTypes}
                />
                <Divider />
              </>
            )}
            <Stack spacing={8}>
              {stockDataDisplayInfo !== undefined &&
                topLevelUnitType !== undefined && (
                  <IconStockCards
                    stockType={topLevelUnitType}
                    stockData={stockDataDisplayInfo}
                  />
                )}
              {stockDataDisplayInfo !== undefined && (
                <Loading
                  loading={
                    initLoadingListings ||
                    !stockDataDisplayInfo.stockDataMap?.size
                  }
                >
                  <StockListingTable
                    key={topLevelUnitType}
                    loadMoreStockDataMap={loadMore}
                    initStockListings={stockDataDisplayInfo}
                  />
                </Loading>
              )}
            </Stack>
          </>
        ) : (
          <Alert severity="info">
            You currently have no available products held on the platform.
          </Alert>
        )}
      </Stack>
    </PortalPageContent>
  );
};
